import { FC, useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import styled from '@emotion/styled';
import OptimizePlanForm from './OptimizePlanForm';
import OptimizePlanTable from './OptimizePlanTable/OptimizePlanTable';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import _ from 'lodash';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { sendNotification } from 'utils/helperMethods';
import { TableHeader } from './OptimizePlanTable/OptimizePlanTableHeader';
import { InputTypes } from './OptimizePlanTable/OptimizeTableContent';
import dayjs from 'dayjs';
import { antIcon } from 'components/pages/Login';
import { useWindowSize } from 'usehooks-ts';
import KPISelector from '../AddEventModal/PromoSections/KPISelector';
import { queryClient } from 'services/reactQueryService';

type Props = {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: any) => void;
    planId: number;
    planYear: number;
    isTpmPlan: boolean;
};
export interface FormData {
    objective: number | null;
    start_date: number | null;
}

export interface OptimisationPlanData {
    name: string;
    label: string;
    default_value: number | null;
    min_value: number | null;
    max_value: number | null;
    single_value: number | null;
    type: InputTypes;
    optimized?: number;
    variance?: number;
    originalValue: number;
}

interface IOptimizeApiData {
    label: string;
    name: string;
    type: InputTypes;
    value: number;
    isSelected: boolean;
}

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 16px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 12px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 8px 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const tableHeaders: TableHeader[] = [
    { key: 'kpi', label: 'KPI', allowSort: true },
    { key: 'orgVal', label: 'Original Value', allowSort: false },
    { key: 'val', label: 'Value', allowSort: false },
];

const OptimizePlanModal: FC<Props> = ({ open, onCancel, onSubmitFinish, planId, planYear, isTpmPlan }) => {
    const [tableData, setTableData] = useState<OptimisationPlanData[]>([]);
    const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({ start_date: null, objective: null });
    const [kpiList, setKpiList] = useState<IOptimizeApiData[]>([]);
    const { width, height } = useWindowSize();
    const percentageHeight = height * 0.9;
    const percentageWidth = width * 0.6;
    const [errorExists, setErrorExists] = useState(false);

    const { loading: tableContentLoading, isError } = useCommonApiHandler({
        method: 'post',
        queryKey: ['get_optimization_org_values', { planId, ...formData }],
        queryConfig: { enabled: !!(formData.start_date && formData.objective) },
        url: `${tradePromoOptimizationUrls.getOptimizationOrgValues}`,
        variables: { ...formData, plan_id: planId, is_tpm_plan: isTpmPlan },
        parserFunction: (res: { data: IOptimizeApiData[]; error?: any }) => {
            try {
                setErrorExists(false);
                const parsedData = res.data.map((item) => ({
                    ...item,
                    isSelected: false,
                }));
                if (tableData?.length > 0) {
                    const updatedData = tableData.map((dataItem: OptimisationPlanData) => {
                        const matchingParsedDatItem = parsedData.find((parsedDataItem: IOptimizeApiData) => {
                            return parsedDataItem.name === dataItem.name;
                        });
                        const originalValue = matchingParsedDatItem?.value ?? dataItem.originalValue;
                        return {
                            ...dataItem,
                            originalValue,
                            min_value: _.round(originalValue - originalValue * 0.2, 2),
                            max_value: _.round(originalValue + originalValue * 0.2, 2),
                        };
                    });
                    setTableData(updatedData);
                }
                setKpiList(parsedData);
                return res;
            } catch (error) {
                setErrorExists(true);
                throw error;
            }
        },
    });

    useEffect(() => {
        if (formData.objective && formData.start_date && errorExists) {
            setErrorExists(false);
        }
    }, [formData]);

    const { mutate: runOptimization, loading: runOptimizationLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.optimizePlan,
        method: 'post',
    });

    const onSubmit = () => {
        const apiPayload = {
            plan_id: planId,
            is_tpm_plan: isTpmPlan,
            objective_function: formData.objective,
            plan_start_timestamp: dayjs(`${planYear}-01-01`).startOf('year').unix(),
            plan_end_timestamp: dayjs(`${planYear}-01-01`).endOf('year').unix(),
            optimization_start_timestamp: formData.start_date,
            asper_customer: process.env.REACT_APP_TENANT_ID,
            constraints: tableData.map((item: OptimisationPlanData) => ({
                name: item.name,
                details: {
                    original_value: item.originalValue ?? 0,
                    min: item.min_value,
                    max: item.max_value,
                    default_value: item.default_value,
                },
            })),
        };
        runOptimization(apiPayload, {
            onSuccess: (data: any) => {
                setFormData({ start_date: null, objective: null });
                setTableData([]);
                onSubmitFinish(data);
            },
            onError: (error: any) => {
                //show error message
                sendNotification('error', 'Something Went Wrong');
            },
        });
    };

    const onKPISelectHandler = (updatedList: string[]) => {
        const selectedKpiData: OptimisationPlanData[] = [];

        setKpiList((prev) =>
            prev.map((kpi) => {
                if (updatedList.includes(kpi.name)) {
                    // Prefer existing table value if available
                    const tableItem = tableData.find((t) => t.name === kpi.name);
                    const minValue = !!tableItem ? tableItem.min_value : _.round(kpi.value - kpi.value * 0.2, 2);
                    const maxValue = !!tableItem ? tableItem.max_value : _.round(kpi.value + kpi.value * 0.2, 2);
                    selectedKpiData.push({
                        name: kpi.name,
                        label: kpi.label,
                        type: kpi.type,
                        originalValue: kpi.value,
                        min_value: minValue,
                        max_value: maxValue,
                        default_value: null,
                        single_value: kpi.value,
                    });
                }
                return { ...kpi, isSelected: updatedList.includes(kpi.name) };
            }),
        );

        setTableData(selectedKpiData);
    };

    //reset all fields to default when popup closes
    useEffect(() => {
        if (open) {
            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['get_optimization_org_values'],
            });
            setErrorExists(false);
        }
    }, [open]);

    return (
        <div>
            <CustomModal
                style={{
                    borderRadius: 8,
                    height: `calc(${percentageHeight}px)`,
                }}
                bodyStyle={{ height: `calc(${percentageHeight}px - 134px)` }}
                width={percentageWidth}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    htmlType: 'submit',
                    disabled:
                        errorExists ||
                        runOptimizationLoading ||
                        tableContentLoading ||
                        !(formData.start_date && formData.objective),
                    style: { borderRadius: 6, minWidth: '140px' },
                    className: 'disabled:bg-red-500 disabled:opacity-50',
                }}
                okText={runOptimizationLoading ? <Spin indicator={antIcon} /> : 'Run Optimization'}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                onCancel={() => {
                    setFormData({ start_date: null, objective: null });
                    setTableData([]);
                    onCancel();
                }}
                title="Optimize"
            >
                <>
                    <OptimizePlanForm formData={formData} setFormData={setFormData} />

                    <div className="bg-backgroundGreyAlt rounded p-3 h-[90%]">
                        <div className="flex justify-end items-center pb-2">
                            <span className="flex-1">Constraints</span>
                            <KPISelector
                                isVisible={isPopoverVisible}
                                setIsVisible={setIsPopoverVisible}
                                kpiList={
                                    kpiList?.map((p) => {
                                        return {
                                            value: p.name?.toString(),
                                            label: p.label,
                                            isSelected: !!tableData.find(
                                                (m: OptimisationPlanData) => m.name === p.name,
                                            ),
                                        };
                                    }) || []
                                }
                                onSubmit={onKPISelectHandler}
                                buttonName="Select Constraints"
                                searchPlaceholder="Search KPI..."
                                variableName={'KPI'}
                                showSelectAll
                                addDisabled={!kpiList.length || tableContentLoading}
                                disableMinimumOneSelectionCondition
                            />
                        </div>
                        {errorExists ? (
                            <div className="flex justify-center items-center h-full text-red-600">
                                {'An unexpected error occurred. Please try again later.'}
                            </div>
                        ) : tableContentLoading ? (
                            <div className="flex justify-center items-center h-full">
                                <Spin />
                            </div>
                        ) : tableData.length ? (
                            <OptimizePlanTable
                                tableData={tableData}
                                setTableData={setTableData}
                                headerColumns={tableHeaders}
                            />
                        ) : (
                            <div className="flex justify-center items-center h-full">Select Constraints</div>
                        )}
                    </div>
                </>
            </CustomModal>
        </div>
    );
};

export default OptimizePlanModal;
